import '@/ui/bootstrapJs';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { NavLink } from 'react-router';

import { getUsername } from '@/app/auth/claims';
import { useNavigateToLogout } from '@/app/auth/interactiveLogout';
import { useTopology } from '@/app/topology';
import { DebugView } from '@/ui';
import { TextWithIcon } from '@/ui/icons/TextWithIcon';

const HELP_LINK = 'https://drive.google.com/drive/folders/1kv-BinYAc0TEAzUkBAWapIsQvRzJS8qL?usp=sharing';

type LoginDisplayProps = {
  hideMenu: () => void;
};

export function LoginDisplay({ hideMenu }: LoginDisplayProps) {
  const auth = useAuth();
  const topology = useTopology();
  const navigateToLogout = useNavigateToLogout();
  const { t } = useTranslation();

  if (!auth.isAuthenticated) {
    return (
      <a href="/authentication/login" className="nav-link">
        Log in
      </a>
    );
  }

  const feedbackLink = t('MainNavigation_FeedbackUrl', { defaultValue: 'https://tally.so/r/wbjXv2' });

  const logOut = () => {
    hideMenu();
    navigateToLogout();
  };

  return (
    <>
      <DebugView>
        <div className="navbar-text">User: {getUsername(auth.user) ?? 'Unknown'}</div>
      </DebugView>

      <div className="nav-item dropdown-center">
        <a
          className="nav-link dropdown-toggle"
          href="#"
          id="profileDropdown"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="fal fa-user pe-1"></i> {auth.user?.profile.name}{' '}
          <i className="far fa-xs fa-chevron-down"></i>
        </a>
        <ul className="dropdown-menu dropdown-menu-lg-end" aria-labelledby="profileDropdown">
          <li className="nav-item">
            <a className="dropdown-item" target="_blank" href={HELP_LINK}>
              <TextWithIcon
                icon="fal fa-question-circle"
                text={t('MainNavigation_Help', { defaultValue: 'Tutorials' })}
                narrowMargin
              />
            </a>
          </li>
          <li className="nav-item">
            <a className="dropdown-item" target="_blank" href={feedbackLink}>
              <TextWithIcon
                icon="fal fa-message-captions"
                text={t('MainNavigation_Feedback', { defaultValue: 'Feedback' })}
                narrowMargin
              />
            </a>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li>
            <NavLink
              to="/settings"
              className={({ isActive }) => (isActive ? 'dropdown-item active' : 'dropdown-item')}
              onClick={hideMenu}
            >
              <TextWithIcon
                icon="fas fa-cog"
                text={t('PageTitle_Settings', { defaultValue: 'Settings' })}
                narrowMargin
              />
            </NavLink>
          </li>
          <li>
            <a
              className="dropdown-item"
              href={`${topology.identityServerUrl}/Account/ChangePassword`}
              onClick={hideMenu}
            >
              <TextWithIcon
                icon="fas fa-lock"
                text={t('Global_ChangePassword_Text', { defaultValue: 'Change password' })}
                narrowMargin
              />
            </a>
          </li>
          <li>
            <button type="button" className="dropdown-item" onClick={logOut}>
              <TextWithIcon
                icon="fal fa-arrow-right-from-bracket"
                text={t('Global_UserLogout_Text', { defaultValue: 'Log out' })}
                narrowMargin
              />
            </button>
          </li>
        </ul>
      </div>
    </>
  );
}
